import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserIsLoggedInQuery } from '@/hooks/queries/use-get-user-is-loggedin.query';
import { usePostLoginMutation } from '@/hooks/mutations/use-post-login.mutation';
import { usePostOtpVerifyMutation } from '@/hooks/mutations/use-post-otp-verify.mutation';
import { usePostOtpResendMutation } from '@/hooks/mutations/use-post-otp-resend.mutation';
import {
  setTokenCookie,
  getTokenCookie,
  removeTokenCookie,
  migrateFromLocalStorage,
  hasTokenCookie,
} from '@/lib/cookies';
import {
  useGetUserDetailQuery,
  type UserDetails,
} from '@/hooks/queries/use-get-user-detail.query';
import { LoadingScreen } from '@/components/ui/loading-screen';
import * as Sentry from '@sentry/react';
import { setTrackingUser } from '@/features/tracking';
import axios from '@/lib/axios';
import {
  getSavedFcmToken,
  clearSavedFcmToken,
  FCM_WEB_REGISTRATION_SESSION_KEY,
} from '@/lib/firebase';
import { clearSessionState } from '@/lib/clear-session-state';

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRoles;
}

interface LoginCredentials {
  email: string;
  role: UserRoles;
}

interface OtpCredentials {
  email: string;
  otp: number;
}

export interface AuthContextType {
  user: UserDetails | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isLoadingLogin: boolean;
  isLoadingOtpVerify: boolean;
  isLoadingOtpResend: boolean;
  login: (
    credentials: LoginCredentials
  ) => Promise<{ success: boolean; email?: string }>;
  logout: () => Promise<{ success: boolean }>;
  verifyOtp: (
    credentials: OtpCredentials
  ) => Promise<{ success: boolean; user?: User }>;
  resendOtp: (email: string, role: UserRoles) => Promise<{ success: boolean }>;
  /** Activate a session from an externally-obtained token (e.g. inline signup) without a page reload. */
  refreshSession: (token: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  /** When true, treat session as logged out even if a stale cookie/query briefly refetches. */
  const [authSessionRevoked, setAuthSessionRevoked] = useState(false);
  // Check if user is logged in
  const { data: authData, refetch: refetchUserLoggedIn } =
    useGetUserIsLoggedInQuery({
      enabled: hasTokenCookie(),
    });
  const userIdRaw = authData?.data?.user;
  const { data: userDetailData } = useGetUserDetailQuery(userIdRaw || '');

  // Mutations
  const loginMutation = usePostLoginMutation();
  const otpVerifyMutation = usePostOtpVerifyMutation();
  const otpResendMutation = usePostOtpResendMutation();

  // Initialize auth state
  useEffect(() => {
    // Check for existing token on mount
    const token = getTokenCookie();

    // If no cookie token, try migration from localStorage
    if (!token) {
      const { migrated } = migrateFromLocalStorage();
      if (migrated) {
        // Token was migrated, refetch user data
        refetchUserLoggedIn().finally(() => {
          setIsInitialized(true);
        });
      } else {
        setIsInitialized(true);
      }
    } else {
      // If token exists, refetch user data
      refetchUserLoggedIn().finally(() => {
        setIsInitialized(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await loginMutation.mutateAsync(credentials);

      if (response.status === 200) {
        return { success: true, email: credentials.email };
      }
      return { success: false };
    } catch {
      return { success: false };
    }
  };

  const verifyOtp = async (credentials: OtpCredentials) => {
    const response = await otpVerifyMutation.mutateAsync(credentials);

    if (response.status === 200) {
      // Store in secure cookie instead of localStorage
      setTokenCookie(response.data.data.token);
      setAuthSessionRevoked(false);
      await queryClient.invalidateQueries({
        queryKey: ['user', 'is-logged-in'],
      });
      setIsAuthenticated(true);
      return { success: true, user: response.data };
    }
    return { success: false };
  };

  const refreshSession = async (token: string) => {
    setTokenCookie(token);
    setAuthSessionRevoked(false);
    setIsAuthenticated(true);
    await queryClient.invalidateQueries({
      queryKey: ['user', 'is-logged-in'],
    });
  };

  // Set Sentry user context when user data is available (only in non-development mode)
  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }
    if (userDetailData?.details) {
      const user = userDetailData.details;
      const fullName = [user.firstname, user.middlename, user.lastname]
        .filter(Boolean)
        .join(' ');
      Sentry.setUser({
        id: user.userid || userIdRaw || undefined,
        email: user.email || undefined,
        username: fullName || user.email || undefined,
      });
    } else if (!isAuthenticated) {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [userDetailData, userIdRaw, isAuthenticated]);

  // Propagate userId to all registered tracking providers
  useEffect(() => {
    const trackedUserId = userDetailData?.details?.userid || userIdRaw;
    setTrackingUser(trackedUserId ? String(trackedUserId) : null);
  }, [userDetailData, userIdRaw, isAuthenticated]);

  const resendOtp = async (email: string, role: UserRoles) => {
    try {
      await otpResendMutation.mutateAsync({ email, role });
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const logout = async () => {
    try {
      const fcmToken = getSavedFcmToken();
      if (fcmToken) {
        try {
          await axios.delete(
            `/notifications/fcm/unregister?token=${encodeURIComponent(fcmToken)}`
          );
        } catch {
          /* non-blocking: token may already be invalid */
        }
        clearSavedFcmToken();
      }
      try {
        sessionStorage.removeItem(FCM_WEB_REGISTRATION_SESSION_KEY);
      } catch {
        /* ignore */
      }
      // Remove token from cookie
      removeTokenCookie();
      // Clear legacy / fallback token storage (setTokenCookie may use fallback)
      try {
        localStorage.removeItem('itria-token');
        localStorage.removeItem('itria-token-fallback');
      } catch {
        /* ignore */
      }
      setIsAuthenticated(false);
      setAuthSessionRevoked(true);
      await clearSessionState(queryClient);
      // Clear Sentry user context (only in non-development mode)
      if (!import.meta.env.DEV) {
        Sentry.setUser(null);
      }
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const contextValue: AuthContextType = {
    user:
      !authSessionRevoked &&
      !!hasTokenCookie() &&
      (isAuthenticated || !!authData?.data?.user)
        ? userDetailData?.details || null
        : null,
    // Require a token so we are never "logged in" after logout clears the
    // cookie (avoids stale React Query data briefly keeping isAuthenticated true).
    // authSessionRevoked blocks refetches if cookie removal races with is-logged-in.
    isAuthenticated:
      !authSessionRevoked &&
      !!hasTokenCookie() &&
      (isAuthenticated || !!authData?.data?.user),
    // Only block on the loading screen during the initial mount. After that,
    // background refetches (e.g. after inline signup's refreshSession) must not
    // flash the full-screen LoadingScreen.
    isInitializing: !isInitialized,
    isLoadingLogin: loginMutation.isPending,
    isLoadingOtpVerify: otpVerifyMutation.isPending,
    isLoadingOtpResend: otpResendMutation.isPending,
    login,
    logout,
    verifyOtp,
    resendOtp,
    refreshSession,
  };

  if (contextValue.isInitializing) {
    return (
      <LoadingScreen
        message="Initializing your experience..."
        animationType="spinner"
      />
    );
  }
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
